import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Stack,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    CircularProgress,
    Avatar,
    Snackbar,
    Grid,
    Chip,
    InputAdornment,
    TablePagination,
    Tooltip,
    Badge,
    Collapse
} from '@mui/material';
import {
    InfoOutlined,
    TrendingUp,
    Payments,
    History,
    Search,
    ContentCopy,
    FilterList,
    ManageAccounts,
    ExpandMore,
    ExpandLess,
    AccountBalance
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useAllPayouts, useCompletePayout, useRejectPayout, useAdminRequestPayout } from 'hooks/usePayouts';
import { useSystemWallet, useMonnifyBalance } from 'hooks/useWallet';
import FilterDrawer from 'components/common/FilterDrawer';
import BankAccountManager from 'views/common/bank-accounts/BankAccountManager';

export default function PayoutManagement() {
    // --- State ---
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Filters
    const [filterOpen, setFilterOpen] = useState(false);
    const [filters, setFilters] = useState({ status: 'all' });
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const [rejectDialog, setRejectDialog] = useState({ open: false, id: null, reason: '' });
    const [withdrawDialog, setWithdrawDialog] = useState({ open: false, amount: '' });
    const [selectedBankAccount, setSelectedBankAccount] = useState(null);
    const [showManageAccounts, setShowManageAccounts] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // --- Search Debounce ---
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedSearch(search), 500);
        return () => clearTimeout(handler);
    }, [search]);

    // --- Data ---
    const { data: payoutRes, isLoading } = useAllPayouts({
        page: page + 1,
        limit: rowsPerPage,
        status: filters.status !== 'all' ? filters.status : undefined,
        search: debouncedSearch || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined
    });

    const payouts = payoutRes?.data || [];
    const totalCount = payoutRes?.total || 0;

    const { data: systemWallet } = useSystemWallet();
    const systemBalance = systemWallet?.availableBalance || 0;

    const { data: monnifyData, isLoading: loadingMonnify } = useMonnifyBalance();
    const monnifyBalance = monnifyData?.responseBody?.availableBalance || 0;

    const completeMutation = useCompletePayout();
    const rejectMutation = useRejectPayout();
    const requestMutation = useAdminRequestPayout();

    // --- Handlers ---
    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        setSnackbar({ open: true, message: 'Account number copied!', severity: 'success' });
    };

    const handleActionSuccess = (msg) => {
        setSnackbar({ open: true, message: msg, severity: 'success' });
        setRejectDialog({ open: false, id: null, reason: '' });
    };

    const handleFilterApply = (newFilters) => {
        setFilters(newFilters);
        setPage(0);
    };

    const filterConfig = [
        {
            name: 'status',
            label: 'Status',
            type: 'select',
            options: [
                { label: 'All Requests', value: 'all' },
                { label: 'Pending', value: 'pending' },
                { label: 'Completed', value: 'completed' },
                { label: 'Rejected', value: 'rejected' }
            ],
            defaultValue: 'all'
        },
        { name: 'startDate', label: 'Start Date', type: 'date' },
        { name: 'endDate', label: 'End Date', type: 'date' }
    ];

    const activeFiltersCount = Object.keys(filters).length;

    return (
        <Box p={3} sx={{ bgcolor: '#fbfbfb', minHeight: '100vh' }}>
            <Box mb={4} display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography variant="h4" fontWeight={900} color="text.primary">
                        Payouts
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage vendor and rider withdrawal requests
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Payments />}
                    onClick={() => setWithdrawDialog({ ...withdrawDialog, open: true })}
                >
                    Withdraw System Funds
                </Button>
            </Box>

            {/* 1. Stats Summary */}
            <Grid container spacing={3} mb={4}>
                <StatCard title="System Available" value={`₦${systemBalance.toLocaleString()}`} icon={<Payments color="primary" />} />
                <StatCard
                    title="Monnify Balance"
                    value={loadingMonnify ? <CircularProgress size={20} /> : `₦${monnifyBalance.toLocaleString()}`}
                    icon={<AccountBalance color="secondary" />} // I need to make sure AccountBalance is imported
                />
                <StatCard
                    title="Volume Requested"
                    value={`₦${(payoutRes?.totalVolume || 0).toLocaleString()}`}
                    icon={<TrendingUp color="secondary" />}
                />
                <StatCard
                    title="Total Disbursed"
                    value={`₦${(payoutRes?.totalPaid || 0).toLocaleString()}`}
                    icon={<Payments color="success" />}
                />
            </Grid>

            {/* 2. Filters & Search */}
            <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                        size="small"
                        placeholder="Search name or account..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ maxWidth: 400, flexGrow: 1 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />

                    <Stack direction="row" spacing={1} alignItems="center">
                        <Badge badgeContent={activeFiltersCount} color="primary">
                            <Button variant="outlined" startIcon={<FilterList />} onClick={() => setFilterOpen(true)}>
                                Filters
                            </Button>
                        </Badge>
                        {activeFiltersCount > 0 && (
                            <Button
                                variant="text"
                                onClick={() => setFilters({ status: 'all' })} // Reset to default
                                color="error"
                                size="small"
                            >
                                Reset
                            </Button>
                        )}
                    </Stack>
                </Stack>
            </Paper>

            <FilterDrawer
                open={filterOpen}
                onClose={() => setFilterOpen(false)}
                onApply={handleFilterApply}
                initialFilters={filters}
                config={filterConfig}
            />

            {/* 3. Table Section */}
            <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ bgcolor: '#fafafa' }}>
                            <TableRow>
                                <TableCell>User</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Bank Details</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" py={5}>
                                        <CircularProgress size={24} />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                payouts.map((p) => (
                                    <TableRow key={p._id} hover>
                                        <TableCell>
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: 'primary.main' }}>
                                                    {p.user?.firstName?.[0]}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="subtitle2">
                                                        {p.user?.firstName} {p.user?.lastName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {p.user?.role}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </TableCell>

                                        <TableCell sx={{ fontWeight: 700 }}>₦{p.amount.toLocaleString()}</TableCell>

                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>
                                                {p.bankName}
                                            </Typography>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {p.accountNumber}
                                                </Typography>
                                                <Tooltip title="Copy Account Number">
                                                    <IconButton size="small" onClick={() => handleCopy(p.accountNumber)}>
                                                        <ContentCopy sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>

                                        <TableCell>
                                            <Typography variant="body2">{format(new Date(p.createdAt), 'dd MMM, yyyy')}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {format(new Date(p.createdAt), 'hh:mm a')}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            <Stack spacing={0.5} alignItems="flex-start">
                                                <Chip
                                                    label={p.status?.toUpperCase()}
                                                    size="small"
                                                    color={
                                                        p.status === 'completed' ? 'success' : p.status === 'rejected' ? 'error' : 'warning'
                                                    }
                                                    variant="filled"
                                                    sx={{ fontWeight: 700, fontSize: '0.65rem' }}
                                                />
                                                {p.status === 'rejected' && p.rejectionReason && (
                                                    <Tooltip title={p.rejectionReason} arrow>
                                                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: 'pointer' }}>
                                                            <InfoOutlined sx={{ fontSize: 14, color: 'error.light' }} />
                                                            <Typography
                                                                variant="caption"
                                                                color="error.main"
                                                                sx={{
                                                                    maxWidth: 80,
                                                                    textOverflow: 'ellipsis',
                                                                    overflow: 'hidden',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                {p.rejectionReason}
                                                            </Typography>
                                                        </Stack>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>

                                        <TableCell align="right">
                                            {p.status === 'pending' && (
                                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        color="success"
                                                        disableElevation
                                                        disabled={completeMutation.isLoading || rejectMutation.isLoading}
                                                        onClick={() =>
                                                            completeMutation.mutate(p._id, {
                                                                onSuccess: () => handleActionSuccess('Paid successfully')
                                                            })
                                                        }
                                                    >
                                                        {completeMutation.isLoading && completeMutation.variables === p._id ? (
                                                            <CircularProgress size={16} color="inherit" />
                                                        ) : (
                                                            'Approve'
                                                        )}
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        color="error"
                                                        disabled={completeMutation.isLoading || rejectMutation.isLoading}
                                                        onClick={() => setRejectDialog({ open: true, id: p._id, reason: '' })}
                                                    >
                                                        Reject
                                                    </Button>
                                                </Stack>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>

            {/* Rejection Modal */}
            <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ ...rejectDialog, open: false })}>
                <DialogTitle>Reason for Rejection</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        sx={{ mt: 1 }}
                        placeholder="e.g. Account name mismatch"
                        value={rejectDialog.reason}
                        onChange={(e) => setRejectDialog({ ...rejectDialog, reason: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectDialog({ ...rejectDialog, open: false })}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        disabled={!rejectDialog.reason || rejectMutation.isLoading}
                        onClick={() => {
                            rejectMutation.mutate(
                                { id: rejectDialog.id, reason: rejectDialog.reason },
                                { onSuccess: () => handleActionSuccess('Request Rejected') }
                            );
                        }}
                    >
                        {rejectMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : 'Reject Request'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Withdraw System Funds Modal */}
            <Dialog
                open={withdrawDialog.open}
                onClose={() => {
                    setWithdrawDialog({ open: false, amount: '' });
                    setSelectedBankAccount(null);
                    setShowManageAccounts(false);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ pb: 1 }}>Request System Payout</DialogTitle>
                <DialogContent dividers>
                    <Alert severity="info" sx={{ mb: 3 }}>
                        Available Balance: <strong>₦{systemBalance.toLocaleString()}</strong>
                    </Alert>

                    {/* Amount Input */}
                    <TextField
                        fullWidth
                        label="Withdrawal Amount"
                        type="number"
                        required
                        size="small"
                        sx={{ mb: 3 }}
                        InputProps={{ startAdornment: <InputAdornment position="start">₦</InputAdornment> }}
                        value={withdrawDialog.amount}
                        onChange={(e) => setWithdrawDialog({ ...withdrawDialog, amount: e.target.value })}
                        error={parseFloat(withdrawDialog.amount) > systemBalance}
                        helperText={parseFloat(withdrawDialog.amount) > systemBalance ? 'Amount exceeds available balance' : ''}
                    />

                    {/* Selected Account Summary */}
                    {selectedBankAccount && (
                        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSelectedBankAccount(null)}>
                            <strong>Sending to:</strong> {selectedBankAccount.bankName} — {selectedBankAccount.accountNumber} (
                            {selectedBankAccount.accountName})
                        </Alert>
                    )}

                    {/* Bank Account Selector */}
                    <BankAccountManager onSelect={(acc) => setSelectedBankAccount(acc)} selectedAccountId={selectedBankAccount?._id} />

                    {/* Manage Accounts Toggle */}
                    <Button
                        fullWidth
                        size="small"
                        startIcon={<ManageAccounts />}
                        endIcon={showManageAccounts ? <ExpandLess /> : <ExpandMore />}
                        onClick={() => setShowManageAccounts(!showManageAccounts)}
                        sx={{ mt: 2, textTransform: 'none', color: 'text.secondary' }}
                    >
                        {showManageAccounts ? 'Hide Account Manager' : 'Manage Saved Accounts'}
                    </Button>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setWithdrawDialog({ open: false, amount: '' });
                            setSelectedBankAccount(null);
                            setShowManageAccounts(false);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={
                            !withdrawDialog.amount ||
                            !selectedBankAccount ||
                            parseFloat(withdrawDialog.amount) > systemBalance ||
                            requestMutation.isLoading
                        }
                        onClick={() => {
                            requestMutation.mutate(
                                {
                                    amount: parseFloat(withdrawDialog.amount),
                                    accountName: selectedBankAccount.accountName,
                                    accountNumber: selectedBankAccount.accountNumber,
                                    bankName: selectedBankAccount.bankName,
                                    bankCode: selectedBankAccount.bankCode
                                },
                                {
                                    onSuccess: () => {
                                        handleActionSuccess('System Payout Requested Successfully');
                                        setWithdrawDialog({ open: false, amount: '' });
                                        setSelectedBankAccount(null);
                                        setShowManageAccounts(false);
                                    }
                                }
                            );
                        }}
                    >
                        {requestMutation.isLoading ? <CircularProgress size={22} /> : 'Submit Request'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}

function StatCard({ title, value, icon }) {
    return (
        <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 3, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'action.hover', color: 'primary.main' }}>{icon}</Avatar>
                <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {title.toUpperCase()}
                    </Typography>
                    <Typography variant="h5" fontWeight={800}>
                        {value}
                    </Typography>
                </Box>
            </Paper>
        </Grid>
    );
}
